<template>
  <div>
    <!-- Filters -->
    <div class="row mb-4">
      <div class="col-md-3">
        <label for="productRuleFilter" class="form-label">Product Rule</label>
        <select
          id="productRuleFilter"
          v-model="selectedProductRule"
          class="form-select"
        >
          <option value="">All Product Rules</option>
          <option
            v-for="rule in availableProductRules"
            :key="rule"
            :value="rule"
          >
            {{ rule }}
          </option>
        </select>
      </div>
      <div class="col-md-3">
        <label for="sourceFilter" class="form-label">Source</label>
        <select id="sourceFilter" v-model="selectedSource" class="form-select">
          <option value="">All Sources</option>
          <option
            v-for="source in availableSources"
            :key="source"
            :value="source"
          >
            {{ source }}
          </option>
        </select>
      </div>
      <div class="col-md-3">
        <label for="importSourceFilter" class="form-label">Import Source</label>
        <select
          id="importSourceFilter"
          v-model="selectedImportSource"
          class="form-select"
        >
          <option value="">All Import Sources</option>
          <option
            v-for="importSource in availableImportSources"
            :key="importSource"
            :value="importSource"
          >
            {{ importSource }}
          </option>
        </select>
      </div>
    </div>

    <!-- Monthly Volume Chart -->
    <LoadingIndicatorComponent v-if="volumeLoading" />
    <div v-else-if="volumeError" class="alert alert-danger">
      {{ volumeError }}
    </div>
    <div v-else-if="monthlyVolumes.length === 0" class="alert alert-info mb-4">
      No monthly volume data available
    </div>
    <div v-else class="mb-4">
      <h5 class="mb-3">Monthly Trading Volumes (Full History)</h5>
      <div class="chart-container">
        <canvas ref="volumeChartCanvas"></canvas>
      </div>
    </div>

    <!-- Volume by Provider Chart -->
    <LoadingIndicatorComponent v-if="providerVolumeLoading" />
    <div v-else-if="providerVolumeError" class="alert alert-danger">
      {{ providerVolumeError }}
    </div>
    <div
      v-else-if="monthlyVolumesByProvider.length === 0"
      class="alert alert-info mb-4"
    >
      No provider volume data available
    </div>
    <div v-else class="mb-4">
      <h5 class="mb-3">Monthly Volumes by Data Provider (Full History)</h5>
      <div class="chart-container">
        <canvas ref="providerChartCanvas"></canvas>
      </div>
    </div>

    <!-- Recent Trades Section -->
    <h5 class="mb-3">Recent Trades</h5>
    <div class="row mb-3">
      <div class="col-md-3">
        <label for="daysFilterTable" class="form-label">Time Range</label>
        <select id="daysFilterTable" v-model="selectedDays" class="form-select">
          <option :value="7">Last 7 days</option>
          <option :value="30">Last 30 days</option>
          <option :value="90">Last 90 days</option>
          <option :value="180">Last 180 days</option>
          <option :value="365">Last 365 days</option>
        </select>
      </div>
    </div>

    <!-- Recent Trades Table -->
    <LoadingIndicatorComponent v-if="tradesLoading" />
    <div v-else-if="tradesError" class="alert alert-danger">
      {{ tradesError }}
    </div>
    <div v-else-if="recentTrades.length === 0" class="alert alert-info">
      No recent trades found
    </div>
    <div v-else>
      <div class="table-responsive">
        <table class="table table-hover">
          <thead>
            <tr>
              <th>Transaction Date</th>
              <th>Product Rule</th>
              <th>Volume</th>
              <th>Price</th>
              <th>Technology</th>
              <th>Origin</th>
              <th>Production Years</th>
              <th>Data Provider</th>
              <th>Import Source</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="trade in recentTrades" :key="trade.id">
              <td>{{ formatDate(trade.transaction_date) }}</td>
              <td>
                <span class="badge bg-primary">{{ trade.product_rule }}</span>
              </td>
              <td>{{ formatNumber(trade.volume) }} {{ trade.volume_unit }}</td>
              <td>
                {{ formatCurrency(trade.price_per_unit, trade.currency) }} /
                {{ trade.price_unit }}
              </td>
              <td>
                <span
                  v-for="tech in trade.technology"
                  :key="tech"
                  class="badge bg-secondary me-1"
                >
                  {{ tech }}
                </span>
              </td>
              <td>
                <span
                  v-for="country in trade.origin"
                  :key="country"
                  class="badge bg-info me-1"
                >
                  {{ country }}
                </span>
              </td>
              <td>
                <span
                  v-for="year in trade.production_years"
                  :key="year"
                  class="badge bg-success me-1"
                >
                  {{ year }}
                </span>
              </td>
              <td>{{ trade.data_provider }}</td>
              <td>
                <span class="badge bg-warning">{{ trade.import_source }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import Chart from "@/chart-setup";
import {
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  useTemplateRef,
  watch,
} from "vue";

import { PALETTE } from "@/composables/chartPalette";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";

interface Trade {
  id: number;
  transaction_id: string;
  transaction_date: string;
  transaction_type: string;
  price_per_unit: string;
  price_unit: string;
  currency: string;
  volume: string;
  volume_unit: string;
  product: string;
  product_rule: string;
  origin: string[];
  technology: string[];
  production_years: number[];
  data_provider: string;
  import_source: string;
}

interface MonthlyVolume {
  month: string;
  total_volume: number;
  trade_count: number;
  avg_price: number;
}

interface MonthlyVolumeByProvider {
  month: string;
  data_provider: string;
  total_volume: number;
}

const { globalErrorHandler } = useGlobalHandlers();

const recentTrades = ref<Trade[]>([]);
const monthlyVolumes = ref<MonthlyVolume[]>([]);
const monthlyVolumesByProvider = ref<MonthlyVolumeByProvider[]>([]);
const availableProductRules = ref<string[]>([]);
const availableSources = ref<string[]>([]);
const availableImportSources = ref<string[]>([]);

const selectedProductRule = ref<string>("");
const selectedSource = ref<string>("");
const selectedImportSource = ref<string>("");
const selectedDays = ref<number>(30);

const isInitializing = ref(true);

const tradesLoading = ref(false);
const volumeLoading = ref(false);
const providerVolumeLoading = ref(false);

const tradesError = ref<string | null>(null);
const volumeError = ref<string | null>(null);
const providerVolumeError = ref<string | null>(null);

const volumeChartCanvas =
  useTemplateRef<HTMLCanvasElement>("volumeChartCanvas");
const providerChartCanvas = useTemplateRef<HTMLCanvasElement>(
  "providerChartCanvas"
);
const volumeChartInstance = shallowRef<Chart | null>(null);
const providerChartInstance = shallowRef<Chart<"bar", number[], string> | null>(
  null
);

const fetchProductRules = async () => {
  try {
    const response = await goSolidApi.get("/api/admin/eac-trade/product-rules");
    availableProductRules.value = response.data.product_rules;

    if (availableProductRules.value.length > 0 && !selectedProductRule.value) {
      selectedProductRule.value =
        availableProductRules.value.find((rule) => rule === "EECS-EU/EEA") ||
        availableProductRules.value[0];
    }
  } catch (error: any) {
    globalErrorHandler(error);
  }
};

const fetchSources = async () => {
  try {
    const response = await goSolidApi.get("/api/admin/eac-trade/sources");
    availableSources.value = response.data.sources;
  } catch (error: any) {
    globalErrorHandler(error);
  }
};

const fetchImportSources = async () => {
  try {
    const response = await goSolidApi.get(
      "/api/admin/eac-trade/import-sources"
    );
    availableImportSources.value = response.data.import_sources;
  } catch (error: any) {
    globalErrorHandler(error);
  }
};

const fetchRecentTrades = async () => {
  tradesLoading.value = true;
  tradesError.value = null;
  try {
    const params: any = { days: selectedDays.value };
    if (selectedProductRule.value) {
      params.product_rule = selectedProductRule.value;
    }
    if (selectedSource.value) {
      params.source = selectedSource.value;
    }
    if (selectedImportSource.value) {
      params.import_source = selectedImportSource.value;
    }

    const response = await goSolidApi.get("/api/admin/eac-trade/trades", {
      params,
    });
    recentTrades.value = response.data.trades;
  } catch (error: any) {
    tradesError.value = "Failed to fetch recent trades";
    globalErrorHandler(error);
  } finally {
    tradesLoading.value = false;
  }
};

const fetchMonthlyVolumes = async () => {
  volumeLoading.value = true;
  volumeError.value = null;
  try {
    const params: any = {};
    if (selectedProductRule.value) {
      params.product_rule = selectedProductRule.value;
    }
    if (selectedSource.value) {
      params.source = selectedSource.value;
    }
    if (selectedImportSource.value) {
      params.import_source = selectedImportSource.value;
    }

    const response = await goSolidApi.get(
      "/api/admin/eac-trade/monthly-volumes",
      { params }
    );
    monthlyVolumes.value = response.data.aggregates;
  } catch (error: any) {
    volumeError.value = "Failed to fetch monthly volumes";
    globalErrorHandler(error);
  } finally {
    volumeLoading.value = false;
  }
};

const fetchMonthlyVolumesByProvider = async () => {
  providerVolumeLoading.value = true;
  providerVolumeError.value = null;
  try {
    const params: any = {};
    if (selectedProductRule.value) {
      params.product_rule = selectedProductRule.value;
    }
    if (selectedSource.value) {
      params.source = selectedSource.value;
    }
    if (selectedImportSource.value) {
      params.import_source = selectedImportSource.value;
    }

    const response = await goSolidApi.get(
      "/api/admin/eac-trade/monthly-volumes-by-provider",
      { params }
    );
    monthlyVolumesByProvider.value = response.data.aggregates;
  } catch (error: any) {
    providerVolumeError.value = "Failed to fetch provider volumes";
    globalErrorHandler(error);
  } finally {
    providerVolumeLoading.value = false;
  }
};

const destroyVolumeChart = () => {
  if (volumeChartInstance.value) {
    volumeChartInstance.value.destroy();
    volumeChartInstance.value = null;
  }
};

const destroyProviderChart = () => {
  if (providerChartInstance.value) {
    providerChartInstance.value.destroy();
    providerChartInstance.value = null;
  }
};

const renderVolumeChart = async () => {
  destroyVolumeChart();

  if (monthlyVolumes.value.length === 0) return;

  await nextTick();

  if (!volumeChartCanvas.value) return;

  const sortedVolumes = [...monthlyVolumes.value].sort(
    (a, b) => new Date(a.month).getTime() - new Date(b.month).getTime()
  );

  const labels = sortedVolumes.map((v) => {
    const date = new Date(v.month);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
    });
  });

  const ctx = volumeChartCanvas.value.getContext("2d");
  if (!ctx) return;

  volumeChartInstance.value = new Chart(ctx, {
    type: "bar",
    data: {
      labels,
      datasets: [
        {
          label: "Total Volume (MWh)",
          data: sortedVolumes.map((v) => v.total_volume),
          backgroundColor: PALETTE.role.focal,
          borderColor: PALETTE.role.focal,
          borderWidth: 1,
          yAxisID: "y",
        },
        {
          label: "Avg Price (EUR/MWh)",
          data: sortedVolumes.map((v) => v.avg_price),
          type: "line",
          backgroundColor: PALETTE.role.reference,
          borderColor: PALETTE.role.reference,
          borderWidth: 2,
          yAxisID: "y1",
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index",
        intersect: false,
      },
      plugins: {
        legend: {
          display: true,
          position: "top",
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              let label = context.dataset.label || "";
              if (label) {
                label += ": ";
              }
              if (context.parsed.y !== null) {
                if (context.datasetIndex === 0) {
                  label += formatNumber(context.parsed.y) + " MWh";
                } else {
                  label += "€" + context.parsed.y.toFixed(4);
                }
              }
              return label;
            },
          },
        },
      },
      scales: {
        y: {
          type: "linear",
          display: true,
          position: "left",
          title: {
            display: true,
            text: "Volume (MWh)",
          },
        },
        y1: {
          type: "linear",
          display: true,
          position: "right",
          title: {
            display: true,
            text: "Price (EUR/MWh)",
          },
          grid: {
            drawOnChartArea: false,
          },
        },
      },
    },
  });
};

const renderProviderChart = async () => {
  destroyProviderChart();

  if (monthlyVolumesByProvider.value.length === 0) return;

  await nextTick();

  if (!providerChartCanvas.value) return;

  const dataByMonth = new Map<string, Map<string, number>>();
  const providers = new Set<string>();

  monthlyVolumesByProvider.value.forEach((item) => {
    providers.add(item.data_provider);
    if (!dataByMonth.has(item.month)) {
      dataByMonth.set(item.month, new Map());
    }
    dataByMonth.get(item.month)!.set(item.data_provider, item.total_volume);
  });

  const sortedMonths = Array.from(dataByMonth.keys()).sort();
  const labels = sortedMonths.map((month) => {
    const date = new Date(month);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
    });
  });

  const colors = PALETTE.categorical;

  const datasets = Array.from(providers).map((provider, index) => ({
    label: provider,
    data: sortedMonths.map(
      (month) => dataByMonth.get(month)?.get(provider) ?? 0
    ),
    backgroundColor: colors[index % colors.length],
    borderColor: colors[index % colors.length],
    borderWidth: 1,
  }));

  const ctx = providerChartCanvas.value.getContext("2d");
  if (!ctx) return;

  providerChartInstance.value = new Chart(ctx, {
    type: "bar",
    data: {
      labels,
      datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index",
        intersect: false,
      },
      plugins: {
        legend: {
          display: true,
          position: "top",
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              let label = context.dataset.label || "";
              if (label) {
                label += ": ";
              }
              if (context.parsed.y !== null) {
                label += formatNumber(context.parsed.y) + " MWh";
              }
              return label;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
        },
        y: {
          stacked: true,
          title: {
            display: true,
            text: "Volume (MWh)",
          },
        },
      },
    },
  });
};

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatNumber = (value: number | string) => {
  const num = typeof value === "string" ? parseFloat(value) : value;
  const safeNum = Number.isNaN(num) ? 0 : num;
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(safeNum);
};

const formatCurrency = (value: string, currency: string) => {
  const num = parseFloat(value);
  const safeNum = Number.isNaN(num) ? 0 : num;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 4,
  }).format(safeNum);
};

watch(monthlyVolumes, () => {
  renderVolumeChart();
});

watch(monthlyVolumesByProvider, () => {
  renderProviderChart();
});

watch([selectedProductRule, selectedSource, selectedImportSource], () => {
  if (isInitializing.value) return;
  fetchRecentTrades();
  fetchMonthlyVolumes();
  fetchMonthlyVolumesByProvider();
});

watch(selectedDays, () => {
  if (isInitializing.value) return;
  fetchRecentTrades();
});

onMounted(async () => {
  await fetchProductRules();
  await fetchSources();
  await fetchImportSources();
  await fetchRecentTrades();
  await fetchMonthlyVolumes();
  await fetchMonthlyVolumesByProvider();
  isInitializing.value = false;
});

onBeforeUnmount(() => {
  destroyVolumeChart();
  destroyProviderChart();
});
</script>

<style lang="scss" scoped>
.badge {
  font-size: 0.75rem;
}

.table {
  font-size: 0.9rem;
}

.chart-container {
  position: relative;
  height: 400px;
}
</style>
